import { Card, CardContent, Chip, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { formatDate } from "../../../utils/formatters";
import React, { useState } from "react";
import { InformationSnippet } from "../../../shared-components/InformationSnippet";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useApiClient } from "../../../api";
import { enqueueSnackbar } from "notistack";
import { AddTagsDialog } from "../../../shared-components/ExpandableTagsBox";
dayjs.extend(customParseFormat);

export const QuizGroupSummary = ({ quizGroup, setQuizGroup }) => {
  const ApiClient = useApiClient();
  const [addTagsOpen, setAddTagsOpen] = useState(false);
  const [newTags, setNewTags] = useState([]);
  const [updating, setUpdating] = useState(false);

  const updateQuiz = async ({ dueDate, tags }) => {
    try {
      setUpdating(true);
      const res = await ApiClient.quiz.updateQuizGroup({
        quizGroup: quizGroup.id,
        dueDate: dayjs(dueDate).format("YYYY-MM-DD"),
        tags: tags.map((t) => t.tag),
      });
      if (res?.success) {
        setQuizGroup({ ...quizGroup, dueDate: dueDate, tags: tags });
        enqueueSnackbar("Successfully updated assignment", {
          variant: "success",
          autoHideDuration: 3000,
        });
        setAddTagsOpen(false);
      } else {
        enqueueSnackbar("Failed to update assignment", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (err) {
      console.error("Failed to update assignment", err);
      enqueueSnackbar("Failed to update assignment", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDueDateChange = (newDueDate) => {
    updateQuiz({
      dueDate: newDueDate,
      tags: quizGroup.tags,
    });
  };

  const handleAddTags = () => {
    const newTagObjects = newTags.map((tagStr) => ({
      id: `temp-${Math.floor(Math.random() * 10000)}`,
      tag: tagStr,
    }));

    const updatedTags = [...quizGroup.tags, ...newTagObjects];

    updateQuiz({
      dueDate: quizGroup.dueDate,
      tags: updatedTags,
    });
  };

  const DueDatePicker = ({ value, onChange }) => {
    const parsedValue =
      value && typeof value === "string"
        ? dayjs(value, ["DD/MM/YYYY", "YYYY-MM-DD", dayjs.ISO_8601], true)
        : dayjs(value);

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          format="DD/MM/YYYY"
          value={parsedValue.isValid() ? parsedValue : null}
          minDate={dayjs()}
          onChange={(newValue) =>
            onChange(newValue ? newValue.toISOString() : null)
          }
        />
      </LocalizationProvider>
    );
  };
  return (
    <Card>
      <AddTagsDialog
        open={addTagsOpen}
        onClose={() => setAddTagsOpen(false)}
        tags={quizGroup.tags}
        subject={quizGroup.schoolClass.subject}
        educationLevel={quizGroup.schoolClass.yearGroup.educationLevel}
        newTags={newTags}
        setNewTags={setNewTags}
        onSave={handleAddTags}
        saving={updating}
      />
      <CardContent>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <Typography variant="h3">{quizGroup.name}</Typography>
            <Chip label={"Quiz"} size="small" />
          </Stack>
        </Stack>
        <Grid container spacing={2} direction={"row"} sx={{ mt: 2, mb: 2 }}>
          <Grid size={{ md: 3, sm: 6, xs: 12 }}>
            <InformationSnippet
              title={"Class"}
              value={quizGroup.schoolClass.name}
            />
          </Grid>
          <Grid size={{ md: 3, sm: 6, xs: 12 }}>
            <InformationSnippet
              title={"Subject"}
              value={quizGroup.schoolClass.subject.name}
            />
          </Grid>
          <Grid size={{ md: 3, sm: 6, xs: 12 }}>
            <InformationSnippet
              title={"Due Date"}
              value={formatDate(quizGroup.dueDate)}
              EditableComponent={DueDatePicker}
              onSave={handleDueDateChange}
            />
          </Grid>
          <Grid size={{ md: 3, sm: 6, xs: 12 }}>
            <InformationSnippet
              title={"Tags"}
              useTagBox
              value={Array.isArray(quizGroup.tags) ? quizGroup.tags : []}
              allowAddTags
              onAddTagsClicked={() => setAddTagsOpen(true)}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
